import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { LoyaltyAppliedOffer } from 'state/loyalty/types';
import logger from 'utils/logger';

import { IAppliedRewards, ICartEntryAdapter, IEntriesIdsMap } from '../types';

// Creates a flat map of all cart entries with sanityId property as key
export const flattenEntriesToMap: (
  acc: IEntriesIdsMap,
  entry: ICartEntryAdapter
) => IEntriesIdsMap = (acc: IEntriesIdsMap, entry: ICartEntryAdapter) => ({
  ...acc,
  [entry.sanityId]: true,
  ...entry.children?.reduce(flattenEntriesToMap, {}),
});

type ParseEntryConfig =
  | { type: LoyaltyPromotionType.OFFER; incentivesToExclude?: IAppliedRewards | null }
  | { type: LoyaltyPromotionType.REWARD; incentivesToExclude?: LoyaltyAppliedOffer[] | null }
  | { type: LoyaltyPromotionType.SYSTEMWIDE_PROMOTION; incentivesToExclude?: never };

// Function maps from ILegacyCartEntry to ICartEntryAdapter to send entries to BE
// Also filters entries that have a reward applied
export const parseEntry = (config: ParseEntryConfig) => {
  const reducer = (
    acc: ICartEntryAdapter[],
    { _id, sanityId, cartId, price = 0, quantity = 1, children = [] }: ILegacyCartEntry
  ): ICartEntryAdapter[] => {
    if (config.type === LoyaltyPromotionType.OFFER) {
      const rewardApplied = config.incentivesToExclude?.[cartId];
      if (rewardApplied?.timesApplied === quantity) {
        return acc;
      }
    } else if (config.type === LoyaltyPromotionType.REWARD) {
      const offerApplied = config.incentivesToExclude?.find(offer => offer.cartId === cartId);
      if (offerApplied) {
        return acc;
      }
    }

    const parsed: ICartEntryAdapter = {
      sanityId: sanityId || _id,
      lineId: cartId,
      price,
      quantity,
    };
    if (children.length) {
      parsed.children = children.reduce(reducer, []);
    }
    return [...acc, parsed];
  };
  return reducer;
};

export const normalizeCartEntriesForOfferEvaluation = (
  cartEntries: ILegacyCartEntry[] | undefined,
  appliedLoyaltyOffers: LoyaltyAppliedOffer[]
): ILegacyCartEntry[] | undefined => {
  const discountOfferCmsId = appliedLoyaltyOffers.find(
    offer => offer.cartId === DISCOUNT_OFFER_CART_ID && offer.cmsId
  )?.cmsId;

  return cartEntries?.flatMap(entry => {
    const children =
      normalizeCartEntriesForOfferEvaluation(entry.children, appliedLoyaltyOffers) ?? [];

    if (entry.cartId !== DISCOUNT_OFFER_CART_ID) {
      return [{ ...entry, children }];
    }

    const normalizedSanityId = entry.sanityId || entry._id || discountOfferCmsId;

    if (!normalizedSanityId) {
      logger.warn({
        message:
          '[normalizeCartEntriesForOfferEvaluation] Dropping discount offer entry missing sanityId',
        context: {
          cartId: entry.cartId,
          lineId: entry.lineId,
        },
      });
      return [];
    }

    return [
      {
        ...entry,
        _id: entry._id || normalizedSanityId,
        sanityId: normalizedSanityId,
        children,
      },
    ];
  });
};
